import { Options, documentToHtmlString } from '@contentful/rich-text-html-renderer';
/**
 * TODO: MARKS breaks in production: https://github.com/contentful/rich-text/issues/395
 * MARKS is exported weird. Since there is no default export nor can MARKS be imported. MARKS needs to be defined inside here.
 * Once Contentful fixes this. We should import MARKS aswell.
 */
import { INLINES, BLOCKS, Document, Block, Inline } from '@contentful/rich-text-types';
import { isInternalURL } from './RouterLinks';

const MARKS = {
  BOLD: 'bold',
  ITALIC: 'italic',
  UNDERLINE: 'underline',
  CODE: 'code',
  SUPERSCRIPT: 'superscript',
  SUBSCRIPT: 'subscript',
};
function convertToHTML(text: string) {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\n/g, '');
}

const richTextToHtmlString = (text: string | Document, links = {}) => {
  const renderOptions = (links: any) => {
    const assetMap = new Map();

    if (links?.assets?.block) {
      for (const asset of links.assets.block) {
        assetMap.set(asset.sys.id, asset);
      }
    }
    if (links?.assets?.hyperlink) {
      for (const asset of links.assets.hyperlink) {
        assetMap.set(asset.sys.id, asset);
      }
    }

    const entryMap = new Map();
    if (links?.entries?.inline) {
      for (const entry of links.entries.inline) {
        entryMap.set(entry.sys.id, entry);
      }
    }

    return {
      renderMark: {
        [MARKS.BOLD]: (text: string) => `<b data-test="render-mark-${text.toLowerCase().split(':')[0]}">${text}</b>`,
        [MARKS.ITALIC]: (text: string) => `<i>${text}</i>`,
        [MARKS.CODE]: convertToHTML,
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node: Block, next: Function) => {
          if (node.content?.[0]?.marks?.[0]?.type === MARKS.CODE) return next(node.content);
          return `<p>${next(node.content).replace(/\n|^(\s*$)/g, '<br/>')}</p>`;
        },
        [BLOCKS.QUOTE]: (node: Block, next: Function) => `<blockquote>${next(node.content)}</blockquote>`,
        [BLOCKS.UL_LIST]: (node: Block, next: Function) => `<ul>${next(node.content)}</ul>`,
        [BLOCKS.OL_LIST]: (node: Block, next: Function) => `<ol>${next(node.content)}</ol>`,
        [BLOCKS.LIST_ITEM]: (node: Block, next: Function) => `<li>${next(node.content)}</li>`,
        [BLOCKS.HR]: (node: Block, next: Function) => `<hr class="my-4 border-gray-300"/>${next(node.content)}`,
        /* Headings uses classes from CHeading component */
        [BLOCKS.HEADING_1]: (node: Block, next: Function) =>
          `<h1 class="text-2xl md:text-[40px] md:leading-[48px] font-bold">${next(node.content)}</h1>`,
        [BLOCKS.HEADING_2]: (node: Block, next: Function) =>
          `<h2 class="text-sm leading-4 lg:text-xl lg:leading-6 font-semibold text-primary-900">${next(
            node.content,
          )}</h2>`,
        [BLOCKS.HEADING_3]: (node: Block, next: Function) =>
          `<h3 class="text-base leading-[20px] md:text-2xl font-semibold">${next(node.content)}</h3>`,
        [BLOCKS.HEADING_4]: (node: Block, next: Function) =>
          `<h4 class="text-sm md:text-xl leading-6 font-semibold">${next(node.content)}</h4>`,
        [BLOCKS.HEADING_5]: (node: Block, next: Function) =>
          `<h5 class="text-xs font-semibold md:text-base md:leading-5">${next(node.content)}</h5>`,
        [BLOCKS.HEADING_6]: (node: Block, next: Function) =>
          `<h6 class="text-[10px] leading-[12px] md:text-sm md:leading-4 font-semibold">${next(node.content)}</h6>`,
        [BLOCKS.TABLE]: (node: Block, next: Function) =>
          `<table class="table-auto border-collapse min-w-full border-solid border border-gray-300 text-left">${next(
            node.content,
          )}</table>`,
        [BLOCKS.TABLE_ROW]: (node: Block, next: Function) =>
          `<tr class="border-collaps border-solid border w-full border-gray-300 text-gray-800 bg-white">${next(
            node.content,
          )}</tr>`,
        [BLOCKS.TABLE_CELL]: (node: Block, next: Function) =>
          `<td class="border-collapse border-solid border border-gray-300 text-gray-800 bg-white p-3 align-middle first-of-type:pl-4 last-of-type:pr-4">${next(
            node.content,
          )}</td>`,
        [BLOCKS.TABLE_HEADER_CELL]: (node: Block, next: Function) =>
          `<th class="border-collapse border-solid border border-gray-300 text-gray-800 bg-primary-50 p-3 first-of-type:pl-4 last-of-type:pr-4 md:text-left">${next(
            node.content,
          )}</th>`,
        [INLINES.EMBEDDED_ENTRY]: (node: Block) => {
          // Only Buttons are handled right now
          // This data can come in 2 formats, from graphQL (in links.entries.inline) or from REST (in node.data.target)
          const entry = entryMap.get(node.data.target.sys.id) ?? node.data.target.fields;
          if (entry?.__typename === 'Button' || entry.url) {
            const inlineClass = entry.inline ? 'c-button--inline' : '';
            const iconClass = entry.icon ? 'c-icon-button' : '';
            const shapeClass = entry.shape === 'squared' ? 'c-button--squared' : '';
            const icon = entry.icon
              ? `<span class="material-symbols-outlined c-icon notranslate c-icon--${entry.size}">${entry.icon}</span>`
              : '';
            return `<a class="c-button ${iconClass} ${inlineClass} ${shapeClass} c-button--${entry.size} c-button--${entry.variant} max-w-8" type="button" href='${entry.url}'>${icon}  ${entry.text}</a>`;
          }
        },
        [INLINES.HYPERLINK]: (node: Inline, next: Function) => {
          const url = node.data.uri.trim();
          if (isInternalURL(url)) {
            return `<a data-nuxt-link href="${url}" class="font-bold underline">${next(node.content)}</a>`;
          }
          return `<a href="${url}" data-test="rich-text-internal-hyperlink" class="font-bold underline" target="_blank" rel="noopener noreferrer">${next(
            node.content,
          )}</a>`;
        },
        [INLINES.ASSET_HYPERLINK]: (node: Inline, next: Function) => {
          const asset = assetMap.get(node.data.target?.sys?.id);
          const url = node.data.target?.fields?.file?.url || asset?.url;
          return `<a href="${url}" class="font-bold underline" target="_blank" rel="noopener noreferrer">${next(
            node.content,
          )}</a>`;
        },
      },
    };
  };
  return documentToHtmlString(text as Document, renderOptions(links) as Options);
};

export { richTextToHtmlString };
